import { useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../ui/dialog";
import { Send, Mail } from "lucide-react";

export default function SendTCPackageButton({ listing, onListingUpdate }) {
  const [open, setOpen] = useState(false);
  const [to, setTo] = useState(listing?.escrow_officer_email || "");
  const [ccTc, setCcTc] = useState(true);
  const [busy, setBusy] = useState(false);

  const tcCollaborators = (listing?.collaborators || []).filter(
    (c) => !c.revoked_at && c.role === "transaction_coordinator"
  );
  const lastSent = listing?.tc_package_last_sent_at;

  const send = async () => {
    if (!to.trim()) {
      toast.error("Enter a recipient email.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/listings/${listing.id}/send-tc-package`, {
        to: to.trim(),
        cc_tc: ccTc,
      });
      toast.success(`TC package emailed to ${data.to}${data.cc?.length ? ` (cc ${data.cc.length})` : ""}`);
      // Refresh listing to pick up tc_package_last_sent_at + activity note
      try {
        const { data: fresh } = await api.get(`/listings/${listing.id}`);
        onListingUpdate(fresh);
      } catch (_) { /* best effort */ }
      setOpen(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not send package.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button
        onClick={() => { setTo(listing?.escrow_officer_email || ""); setOpen(true); }}
        className="rounded-full px-5 text-white"
        style={{ backgroundColor: "#010082" }}
        data-testid="send-tc-package-button"
      >
        <Send className="h-3.5 w-3.5 mr-1.5" />
        Send TC package
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="send-tc-package-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading tracking-tight">Send Transaction package</DialogTitle>
            <DialogDescription>
              Emails a polished HTML with the ratified RPA link + every mutual escrow milestone (open / complete / waived). No pre-listing data is included.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-1">
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold mb-1.5 block">
                Recipient (defaults to Escrow Officer)
              </Label>
              <Input
                type="email"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                data-testid="tc-package-to"
                placeholder="escrow@oldrepublictitle.com"
                className="h-10 border-stone-300"
              />
            </div>
            {tcCollaborators.length > 0 && (
              <label className="flex items-start gap-3 rounded-md border border-stone-200 bg-stone-50 p-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={ccTc}
                  onChange={(e) => setCcTc(e.target.checked)}
                  data-testid="tc-package-cc-tc"
                  className="mt-0.5 h-4 w-4 accent-[#010082]"
                />
                <span className="text-xs text-stone-700 leading-relaxed">
                  Also CC the Transaction Coordinator{tcCollaborators.length > 1 ? "s" : ""}:
                  <span className="font-medium text-stone-900"> {tcCollaborators.map((c) => c.name).join(", ")}</span>
                </span>
              </label>
            )}
            {lastSent && (
              <p className="text-[11px] text-stone-500 flex items-center gap-1.5">
                <Mail className="h-3 w-3" />
                Last sent {new Date(lastSent).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
              </p>
            )}
          </div>
          <DialogFooter className="mt-5">
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-full border-stone-300">Cancel</Button>
            <Button
              onClick={send}
              disabled={busy}
              className="rounded-full text-white px-5"
              style={{ backgroundColor: "#010082" }}
              data-testid="tc-package-submit"
            >
              {busy ? "Sending…" : "Send package"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
